import { computed } from 'vue'
import { useRoute } from 'vue-router'

/**
 * The URL is the ultimate source of truth for information on the active
 * workspace/project/entity IDs, as the rest of the frontend loads data based
 * on these IDs.
 *
 * Getting the IDs from here is quicker than getting them from the stores,
 * as the stores often won't be populated until the full workspace/entity/project
 * has been loaded. By using this composable we can load data asynchronously
 * instead of waiting for the workspace to be fully loaded before we fetch
 * anything inside of it.
 *
 * @deprecated URL is not the ultimate source of truth anymore, other than for workspace id.
 * Use data from stores such as `useProject` or injections such as `useWorkspaceContext` when available.
 * In Cases, projects and entities that are loaded in the sidebar will not be reflected in the URL.
 * Fetching methods can be improved for performance, but we have to decouple components from the router.
 *
 * NOTE: We do not use invariant in these computeds, as checking for type directly and throwing
 * gives us a better type guard.
 */
export const useRouteParams = () => {
  const route = useRoute()

  const parentEntityId = computed(() => {
    if (Array.isArray(route.query.parentEntityId)) {
      throw new Error('Multiple parentEntityId values found in route')
    }
    return route.query.parentEntityId || undefined
  })

  const parentProjectId = computed(() => {
    if (Array.isArray(route.params.parentProjectId)) {
      throw new Error('Multiple parentProjectId values found in route')
    }
    return route.params.parentProjectId || undefined
  })

  const viewId = computed(() => {
    if (Array.isArray(route.params.viewId)) {
      throw new Error('Multiple viewId values found in route')
    }
    return route.params.viewId || undefined
  })

  const parentViewId = computed(() => {
    if (Array.isArray(route.query.parentViewId)) {
      throw new Error('Multiple parentViewId values found in route')
    }
    return route.query.parentViewId || undefined
  })

  const entityId = computed(() => {
    if (Array.isArray(route.params.entityId)) {
      throw new Error('Multiple entityId values found in route')
    }
    return route.params.entityId || undefined
  })

  const spaceId = computed(() => {
    if (Array.isArray(route.params.spaceId)) {
      throw new Error('Multiple spaceId values found in route')
    }
    return route.params.spaceId || undefined
  })

  return {
    entityId,
    parentEntityId,
    parentProjectId,
    parentViewId,
    viewId,
    spaceId,
  }
}
